import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import { getApiBaseUrl } from '../../apiBase';
import { formatDate, getLevelBadge } from './bugHelpers';
import './BugReportsPage.css';

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
}

function StatCard({ label, value }) {
    return (
        <div className="bug-stat-card">
            <div style={{ fontSize: 13, color: '#64748b' }}>{label}</div>
            <div className="value">{value}</div>
        </div>
    );
}

const DEFAULT_BONUS = {
    crash: 50,
    bug: 30,
    ui: 15,
    translation: 10,
    suggestion: 5,
};

const DEFAULT_LEVELS = {
    bronze: 0,
    silver: 200,
    gold: 500,
    platinum: 1000,
};

export default function TestersPage() {
    const { t } = useTranslation('adminBugs');
    const [stats, setStats] = useState(null);
    const [testers, setTesters] = useState([]);
    const [bonusSettings, setBonusSettings] = useState(DEFAULT_BONUS);
    const [levelSettings, setLevelSettings] = useState(DEFAULT_LEVELS);

    const load = useCallback(async () => {
        const api = adminApi();
        const [statsRes, listRes] = await Promise.all([
            api.get('/admin/testers/stats'),
            api.get('/admin/testers'),
        ]);
        setStats(statsRes.data);
        setTesters(listRes.data);
    }, []);

    useEffect(() => {
        load().catch(console.error);
    }, [load]);

    async function addPoints(id, delta) {
        await adminApi().post(`/admin/testers/${id}/points`, { delta });
        load();
    }

    async function toggleStatus(id) {
        await adminApi().post(`/admin/testers/${id}/toggle`);
        load();
    }

    function saveBonusSettings() {
        localStorage.setItem('sp_tester_bonus_settings', JSON.stringify(bonusSettings));
        localStorage.setItem('sp_tester_level_settings', JSON.stringify(levelSettings));
        alert(t('saveSettings'));
    }

    useEffect(() => {
        try {
            const b = localStorage.getItem('sp_tester_bonus_settings');
            const l = localStorage.getItem('sp_tester_level_settings');
            if (b) setBonusSettings(JSON.parse(b));
            if (l) setLevelSettings(JSON.parse(l));
        } catch { /* ignore */ }
    }, []);

    return (
        <OwnerPageLayout title={t('testersTitle')} maxWidth={1200}>
            <p style={{ marginBottom: 16 }}>
                <a href="/admin/integrations" style={{ color: '#EB3333', fontWeight: 600 }}>
                    ⚙️ Telegram-уведомления — в настройках ключей API
                </a>
            </p>
            <div className="stats-row">
                <StatCard label="Всего тестировщиков" value={stats?.totalTesters ?? '—'} />
                <StatCard label="Активных за неделю" value={stats?.activeWeek ?? '—'} />
                <StatCard label="Репортов всего" value={stats?.totalReports ?? '—'} />
                <StatCard label="Принято репортов" value={stats?.acceptedReports ?? '—'} />
            </div>

            <h2>🏆 Таблица лидеров</h2>
            <table className="bugs-table">
                <thead>
                    <tr>
                        <th>Место</th>
                        <th>Тестировщик</th>
                        <th>Уровень</th>
                        <th>Очки</th>
                        <th>Репортов</th>
                        <th>Принято</th>
                        <th>Активность</th>
                        <th>Действия</th>
                    </tr>
                </thead>
                <tbody>
                    {testers.map((tester, idx) => (
                        <tr key={tester.id}>
                            <td>{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : `#${idx + 1}`}</td>
                            <td>
                                <div>{tester.name}</div>
                                <div className="bug-device">{tester.email}</div>
                            </td>
                            <td>{getLevelBadge(tester.level)}</td>
                            <td>💎 {tester.totalPoints}</td>
                            <td>{tester.totalBugsReported}</td>
                            <td>
                                {tester.acceptedBugs}
                                {tester.totalBugsReported > 0 && (
                                    <span className="bug-device">
                                        {' '}({Math.round(tester.acceptedBugs / tester.totalBugsReported * 100)}%)
                                    </span>
                                )}
                            </td>
                            <td>{formatDate(tester.lastActivityAt)}</td>
                            <td>
                                <button type="button" onClick={() => addPoints(tester.id, 50)}>💎 +50</button>
                                {' '}
                                <button type="button" onClick={() => toggleStatus(tester.id)}>
                                    {tester.isActive ? '🚫' : '✅'}
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div style={{ marginTop: 32 }}>
                <h2>⚙️ {t('bonusSettings')}</h2>
                <div style={{ display: 'grid', gap: 10, maxWidth: 400 }}>
                    {Object.entries(bonusSettings).map(([key, val]) => (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ minWidth: 120 }}>{key}</span>
                            <input
                                type="number"
                                value={val}
                                onChange={(e) => setBonusSettings({ ...bonusSettings, [key]: Number(e.target.value) })}
                            />
                            <span>очков</span>
                        </div>
                    ))}
                </div>

                <h3 style={{ marginTop: 24 }}>{t('levelSettings')}</h3>
                <div style={{ display: 'grid', gap: 10, maxWidth: 400 }}>
                    {Object.entries(levelSettings).map(([level, min]) => (
                        <div key={level} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ minWidth: 120 }}>{getLevelBadge(level)}</span>
                            <span>от</span>
                            <input
                                type="number"
                                value={min}
                                onChange={(e) => setLevelSettings({ ...levelSettings, [level]: Number(e.target.value) })}
                            />
                            <span>очков</span>
                        </div>
                    ))}
                </div>

                <button type="button" className="bug-submit" style={{ marginTop: 16, maxWidth: 240 }} onClick={saveBonusSettings}>
                    💾 {t('saveSettings')}
                </button>
            </div>
        </OwnerPageLayout>
    );
}
